import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Video, Volleyball, Zap } from 'lucide-react';
import { useSessions } from '../../hooks/useSessions';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { SessionCard } from '../../components/beachvision/SessionCard';
import { LoadingSkeleton } from '../../components/beachvision/LoadingSkeleton';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
const RECENT_LIMIT = 3;

function DashboardInner() {
  const { data: sessions, isLoading, isError, refetch } = useSessions();

  const completed = useMemo(
    () => (sessions || []).filter((s) => s.status === 'complete'),
    [sessions]
  );

  const sparkData = useMemo(() => {
    return [...completed]
      .filter((s) => s.overall_score != null)
      .sort(
        (a, b) =>
          new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
      )
      .map((s, i) => ({ i, score: Number(s.overall_score) }));
  }, [completed]);

  const latestScore =
    sparkData.length > 0 ? sparkData[sparkData.length - 1].score : null;

  const weeklyGoal = 12;
  const weeklyDone = Math.min(completed.length, weeklyGoal);
  const weeklyPct = Math.round((weeklyDone / weeklyGoal) * 100);

  const recentSessions = useMemo(
    () => (sessions || []).slice(0, RECENT_LIMIT),
    [sessions]
  );

  return (
    <>
      <header className="flex items-center px-4 pt-6 pb-4">
        <div className="flex items-center gap-2">
          <Volleyball className="h-8 w-8 text-beachvision-coral" aria-hidden />
          <h1 className="font-display font-black text-xl tracking-tight text-beachvision-primary-light">
            Beachvision
          </h1>
        </div>
      </header>

      {isLoading ? (
        <div className="px-4 space-y-4">
          <LoadingSkeleton className="h-28 w-full" />
          <LoadingSkeleton className="h-28 w-full" />
          <LoadingSkeleton className="h-24 w-full" />
        </div>
      ) : isError ? (
        <div className="px-4 text-center py-12">
          <p className="text-beachvision-muted mb-4">Could not load sessions.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3 font-display font-bold"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* Score cards */}
          <div className="px-4 grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-beachvision-surface-low p-4 relative overflow-hidden">
              <p className="text-xs font-display font-bold uppercase text-beachvision-coral tracking-wide">
                Performance index
              </p>
              <p className="text-3xl font-display font-black text-white mt-1">
                {latestScore != null ? latestScore.toFixed(1) : '—'}
              </p>
              <p className="text-xs text-beachvision-muted mt-1">Rolling trend</p>
              <div className="h-14 mt-2 w-full relative z-10">
                {sparkData.length > 1 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sparkData}>
                      <Tooltip
                        contentStyle={{
                          background: '#232a38',
                          border: 'none',
                          borderRadius: 8,
                          color: '#fff',
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#4ECDC4"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-beachvision-muted pt-3">Not enough data yet</p>
                )}
              </div>
              <Zap className="absolute bottom-2 right-2 h-16 w-16 text-beachvision-coral/10" />
            </div>
            <div className="rounded-2xl bg-beachvision-teal/15 p-4 flex flex-col justify-between">
              <p className="text-xs font-display font-bold uppercase text-beachvision-teal tracking-wide">
                Weekly drills
              </p>
              <p className="text-3xl font-display font-black text-white mt-1">
                {weeklyDone}
                <span className="text-lg text-beachvision-muted font-bold">/{weeklyGoal}</span>
              </p>
              <div className="h-2 mt-3 rounded-full bg-black/25 overflow-hidden">
                <div
                  className="h-full bg-beachvision-teal rounded-full transition-all"
                  style={{ width: `${weeklyPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Upload CTA */}
          <motion.div
            className="px-4 mt-6"
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <Link
              to="/upload/"
              className="flex items-center justify-center gap-3 w-full bg-beachvision-cta text-beachvision-on-coral rounded-2xl px-6 py-4 font-display font-black uppercase tracking-wide text-base shadow-lg shadow-beachvision-coral/20"
            >
              <Video className="h-6 w-6" />
              Analyze new video
            </Link>
          </motion.div>

          {/* Recent sessions */}
          <section className="px-4 mt-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-display font-bold uppercase tracking-wide text-white">
                Recent sessions
              </h2>
              {sessions && sessions.length > RECENT_LIMIT && (
                <Link
                  to="/history/"
                  className="text-xs font-display font-bold uppercase text-beachvision-coral"
                >
                  View all
                </Link>
              )}
            </div>
            {!sessions || sessions.length === 0 ? (
              <div className="rounded-2xl bg-beachvision-surface-low p-8 text-center">
                <div className="text-5xl mb-3" aria-hidden>
                  🏐
                </div>
                <p className="text-white font-display font-semibold mb-1">
                  Upload your first session to get started
                </p>
                <p className="text-beachvision-muted text-sm">
                  We will break down your technique in minutes.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentSessions.map((s) => (
                  <SessionCard key={s.id} {...s} />
                ))}
                {sessions.length > RECENT_LIMIT && (
                  <Link
                    to="/history/"
                    className="block text-center text-xs font-display font-bold uppercase text-beachvision-coral py-2"
                  >
                    View all {sessions.length} sessions →
                  </Link>
                )}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

export default function DashboardPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <DashboardInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
